/**
 * Voice call history + transcripts — the client of /api/voice-calls.
 *
 * The call itself is LiveKit's; this is its memory. VoiceRoomContext calls
 * `joinVoiceCall` once the media is up and `leaveVoiceCall` on the way out, the
 * microphone recorder posts chunks with `uploadVoiceChunk`, and the history
 * pages read everything back. The audio never comes back down: only text is
 * stored server-side.
 *
 * The tail of the file is the call's afterword: the task suggestions the AI
 * drew from the transcript once the call ended, decided by a person on the
 * call page, and „minuta discuției” — the same Word document the meetings
 * module writes, written for a call.
 */
import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';
import { downloadMinutesDocx } from './meetingLogApi';
import type {
  AcceptTaskSuggestionInput,
  MeetingMinutes,
  MeetingTaskSuggestion,
} from './meetingLogApi';

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function jsonHeaders(): Record<string, string> {
  return { ...authHeaders(), 'Content-Type': 'application/json' };
}

export interface VoiceCallParticipant {
  userId: string;
  name: string;
  joinedAt: string;
  leftAt: string | null;
}

export interface VoiceCallSummary {
  id: string;
  /** Null once the channel was deleted; `channelName` still says which it was. */
  channelId: string | null;
  channelName: string;
  startedAt: string;
  /** Null while the call is still going on. */
  endedAt: string | null;
  durationMs: number;
  participants: VoiceCallParticipant[];
  segmentCount: number;
  /** Tasks the AI proposed from this call that nobody has decided on yet. */
  pendingSuggestionCount: number;
}

export interface VoiceTranscriptSegment {
  id: string;
  /** Firmio Meet lines carry the participant row instead of (or as well as) a user. */
  participantId?: string;
  userId: string | null;
  speakerName: string;
  startedAt: string;
  durationMs: number;
  text: string;
  /** Server time; monotonic, which is what the live poll pages on. */
  createdAt: string;
}

export interface VoiceCallDetail extends VoiceCallSummary {
  segments: VoiceTranscriptSegment[];
}

export async function fetchVoiceCalls(input: {
  channelId?: string;
  limit?: number;
}): Promise<{ calls: VoiceCallSummary[]; transcriptionEnabled: boolean }> {
  const params = new URLSearchParams();
  if (input.channelId) params.set('channelId', input.channelId);
  if (input.limit) params.set('limit', String(input.limit));
  const qs = params.toString();
  const res = await apiFetch<
    Envelope<{ calls: VoiceCallSummary[]; transcriptionEnabled: boolean }>
  >(`/api/voice-calls${qs ? `?${qs}` : ''}`, { headers: authHeaders() });
  return {
    calls: res.data?.calls ?? [],
    transcriptionEnabled: res.data?.transcriptionEnabled ?? false,
  };
}

export async function fetchVoiceCall(callId: string): Promise<VoiceCallDetail> {
  const res = await apiFetch<Envelope<VoiceCallDetail>>(
    `/api/voice-calls/${encodeURIComponent(callId)}`,
    { headers: authHeaders() },
  );
  return res.data;
}

/** Lines stored after `since`, and whether the call has ended meanwhile. */
export async function fetchVoiceSegmentsSince(
  callId: string,
  since: string | null,
): Promise<{ segments: VoiceTranscriptSegment[]; ended: boolean }> {
  const qs = since ? `?since=${encodeURIComponent(since)}` : '';
  const res = await apiFetch<Envelope<{ segments: VoiceTranscriptSegment[]; ended: boolean }>>(
    `/api/voice-calls/${encodeURIComponent(callId)}/segments${qs}`,
    { headers: authHeaders() },
  );
  return { segments: res.data?.segments ?? [], ended: res.data?.ended ?? false };
}

export async function joinVoiceCall(
  channelId: string,
): Promise<{ callId: string; transcriptionEnabled: boolean }> {
  const res = await apiFetch<Envelope<{ callId: string; transcriptionEnabled: boolean }>>(
    '/api/voice-calls/join',
    { method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ channelId }) },
  );
  return res.data;
}

/**
 * `keepalive` lets the request outlive the page: it is how a tab being closed
 * still says it left, instead of leaving the call "live" until the sweep.
 */
export async function leaveVoiceCall(
  callId: string,
  opts: { keepalive?: boolean } = {},
): Promise<void> {
  await apiFetch<Envelope<{ ended: boolean }>>(
    `/api/voice-calls/${encodeURIComponent(callId)}/leave`,
    { method: 'POST', headers: authHeaders(), keepalive: opts.keepalive },
  );
}

/**
 * One recorded chunk of our own microphone. The body is the raw Blob with its
 * own content type — no multipart — and the answer is the stored line, or
 * `null` when the chunk carried no speech.
 */
export async function uploadVoiceChunk(input: {
  callId: string;
  audio: Blob;
  startedAt: Date;
  durationMs: number;
}): Promise<VoiceTranscriptSegment | null> {
  const params = new URLSearchParams({
    startedAt: input.startedAt.toISOString(),
    durationMs: String(Math.max(1, Math.round(input.durationMs))),
  });
  const res = await apiFetch<Envelope<{ segment: VoiceTranscriptSegment | null }>>(
    `/api/voice-calls/${encodeURIComponent(input.callId)}/segments?${params.toString()}`,
    {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': input.audio.type || 'audio/webm' },
      body: input.audio,
    },
  );
  return res.data?.segment ?? null;
}

// --- Task suggestions --------------------------------------------------------
// What the AI proposed after reading the call, and the person's decision on
// each one. The same rows, the same cards and the same table as a meeting's
// suggestions, so the meetings types are the types here too.

function suggestionsUrl(callId: string, tail = ''): string {
  return `/api/voice-calls/${encodeURIComponent(callId)}/task-suggestions${tail}`;
}

export async function fetchCallTaskSuggestions(callId: string): Promise<MeetingTaskSuggestion[]> {
  const res = await apiFetch<Envelope<MeetingTaskSuggestion[]>>(suggestionsUrl(callId), {
    headers: authHeaders(),
  });
  return Array.isArray(res.data) ? res.data : [];
}

/** Re-read the transcript, replacing the pending suggestions. */
export async function generateCallTaskSuggestions(
  callId: string,
): Promise<MeetingTaskSuggestion[]> {
  const res = await apiFetch<Envelope<MeetingTaskSuggestion[]>>(
    suggestionsUrl(callId, '/generate'),
    { method: 'POST', headers: jsonHeaders(), body: '{}' },
  );
  return Array.isArray(res.data) ? res.data : [];
}

export async function acceptCallTaskSuggestion(
  callId: string,
  suggestionId: string,
  input: AcceptTaskSuggestionInput,
): Promise<MeetingTaskSuggestion> {
  const res = await apiFetch<Envelope<MeetingTaskSuggestion>>(
    suggestionsUrl(callId, `/${encodeURIComponent(suggestionId)}/accept`),
    { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(input) },
  );
  return res.data;
}

export async function dismissCallTaskSuggestion(
  callId: string,
  suggestionId: string,
): Promise<MeetingTaskSuggestion> {
  const res = await apiFetch<Envelope<MeetingTaskSuggestion>>(
    suggestionsUrl(callId, `/${encodeURIComponent(suggestionId)}/dismiss`),
    { method: 'POST', headers: jsonHeaders(), body: '{}' },
  );
  return res.data;
}

export async function restoreCallTaskSuggestion(
  callId: string,
  suggestionId: string,
): Promise<MeetingTaskSuggestion> {
  const res = await apiFetch<Envelope<MeetingTaskSuggestion>>(
    suggestionsUrl(callId, `/${encodeURIComponent(suggestionId)}/restore`),
    { method: 'POST', headers: jsonHeaders(), body: '{}' },
  );
  return res.data;
}

// --- Minutes -----------------------------------------------------------------
// „Minuta discuției”: the AI writes a high-level record of the call from its
// transcript. The same rows, the same type and the same Word document as a
// meeting's minutes — only the owner and the URL differ.

function callMinutesUrl(callId: string, tail = ''): string {
  return `/api/voice-calls/${encodeURIComponent(callId)}/minutes${tail}`;
}

export async function fetchCallMinutes(callId: string): Promise<MeetingMinutes[]> {
  const res = await apiFetch<Envelope<MeetingMinutes[]>>(callMinutesUrl(callId), {
    headers: authHeaders(),
  });
  return Array.isArray(res.data) ? res.data : [];
}

/** One paid generation. 409 while the call is on or when too little was said. */
export async function generateCallMinutes(callId: string): Promise<MeetingMinutes> {
  const res = await apiFetch<Envelope<MeetingMinutes>>(callMinutesUrl(callId, '/generate'), {
    method: 'POST',
    headers: jsonHeaders(),
    body: '{}',
  });
  return res.data;
}

/** Email the document to the people who were in the room. */
export async function emailCallMinutes(
  callId: string,
  minutes: MeetingMinutes,
): Promise<MeetingMinutes> {
  const res = await apiFetch<Envelope<MeetingMinutes>>(
    callMinutesUrl(callId, `/${encodeURIComponent(minutes.id)}/email`),
    { method: 'POST', headers: jsonHeaders(), body: '{}' },
  );
  return res.data;
}

export async function downloadCallMinutesDocx(
  callId: string,
  minutes: MeetingMinutes,
): Promise<void> {
  return downloadMinutesDocx(
    callMinutesUrl(callId, `/${encodeURIComponent(minutes.id)}/docx`),
    minutes.fileName,
  );
}

/** ADMIN only — the server rejects anyone else with 403. */
export async function deleteVoiceCall(callId: string): Promise<void> {
  await apiFetch<Envelope<{ deleted: boolean }>>(`/api/voice-calls/${encodeURIComponent(callId)}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
}
